/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import DS from 'ember-data';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import SubscriptionTopicModel from 'cio/models/subscription-topic';
import SubscriptionCenterService from 'cio/services/subscription-center';
import ApiErrorsService from 'cio/services/api-errors';
import AnalyticsService from 'cio/services/analytics';
import FeaturesService from 'ember-feature-flags/services/features';
import AccountService from 'cio/services/account';
import EnvironmentService from 'cio/services/environment';
import AuthService from 'cio/services/auth';
import RouterService from '@ember/routing/router-service';
import { dropTask } from 'ember-concurrency';
import { getTrackApiHostForDataCenter } from 'cio/utils/data-centers';
import pluralize from 'cio/utils/pluralize';

interface ViewArgs {
  topics?: SubscriptionTopicModel[];
  newTopic?: SubscriptionTopicModel;
  isAddingTopic: boolean;
  createNewTopic: () => unknown;
  onCreate: (record: SubscriptionTopicModel) => unknown;
  onCreateCancel: () => unknown;
  subscribedPeopleTotal: number;
  showEnableCenterModal: boolean;
  toggleShowEnableCenterModal: (show: boolean) => unknown;
}

export default class ViewComponent extends Component<ViewArgs> {
  @service account!: AccountService;
  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service features!: FeaturesService;
  @service router!: RouterService;
  @service store!: DS.Store;
  @service subscriptionCenter!: SubscriptionCenterService;

  @tracked showDisableCenterModal = false;
  @tracked animateTopicTable = false;

  constructor(owner: unknown, args: ViewArgs) {
    super(owner, args);

    this.subscriptionCenter.fetchMissingTopicsTask.perform();
  }

  get subscriptionCenterEnabled() {
    return this.subscriptionCenter.enabled;
  }

  get isFetchingMissingTopics() {
    return this.subscriptionCenter.fetchMissingTopicsTask.isRunning;
  }

  get shouldShowMissingTopicsBanner() {
    if (this.subscriptionCenter.initialized && !this.subscriptionCenter.enabled) {
      return this.subscriptionCenter.hasEntitiesWithoutTopics;
    }
    return false;
  }

  get trackAPISubPreferencesDraftPreviewURL() {
    let payload = { envId: parseInt(this.environment.current.id ?? '0'), uuid: window.UUID.generate() };
    let token = btoa(JSON.stringify(payload));

    let url = new URL(
      'manage_subscription_preferences',
      getTrackApiHostForDataCenter(this.account.current?.dataCenter),
    );
    url.searchParams.append('token', token);
    url.searchParams.append('previewType', 'draft');

    return url.toString();
  }

  get totalActiveSummary() {
    let { campaigns, newsletters, actions } = this.subscriptionCenter.entitiesWithoutTopics;
    let totalCampaigns = campaigns + newsletters ?? 0;
    let message = '';
    if (totalCampaigns > 0) {
      message += `There ${pluralize(totalCampaigns, 'is', 'are')} ${totalCampaigns} running ${pluralize(
        totalCampaigns,
        'campaign or broadcast',
        'campaigns or broadcasts',
      )}`;
      if (actions > 0) {
        message += ` and ${actions} ${pluralize(actions, 'message', 'messages')}`;
      }
    } else if (actions > 0) {
      message += `There ${pluralize(actions, 'is', 'are')} ${actions} ${pluralize(actions, 'message', 'messages')}`;
    }
    if (message.length > 0) {
      message += ` that ${pluralize(actions + totalCampaigns ?? 1, 'needs', 'need')} attention`;
    }
    return message;
  }

  @action
  startSetBulkPreferences() {
    this.analytics.track('subscription_center', 'set_bulk_preferences_started');
    this.router.transitionTo('workspace-settings.subscription_center.topics.set_preferences');
  }

  @action
  trackPreviewDraftLink() {
    this.analytics.track('subscription_center', 'preview_draft_clicked');
  }

  toggleSubscriptionCenterTask = dropTask(async () => {
    if (this.subscriptionCenter.enabled) {
      this.subscriptionCenter.disable();
      this.animateTopicTable = false;
    } else {
      this.subscriptionCenter.enable();
    }
  });
}
